import { Link } from 'react-router-dom';

import type { AvatarItem } from '@/services/avatarApi';
import { AvatarBadges } from '@/components/avatars/AvatarBadges';
import { cn } from '@/lib/cn';

interface AvatarCardProps {
  avatar: AvatarItem;
  /**
   * `list` (default) = full-bleed single-column card matching the original
   * layout. `grid` = compact square tile suited for a 2-column grid; ignores
   * the avatar's 3:4 aspect ratio to keep tile heights consistent.
   */
  variant?: 'list' | 'grid';
  className?: string;
}

export function AvatarCard({ avatar, variant = 'list', className }: AvatarCardProps) {
  const isGrid = variant === 'grid';
  const aspectClass = isGrid
    ? 'aspect-square'
    : avatar.aspectRatio === '3:4'
      ? 'aspect-[3/4]'
      : 'aspect-square';

  return (
    <Link
      to={`/avatars/${avatar._id}`}
      className={cn(
        'block overflow-hidden rounded-2xl border border-black/40 bg-black',
        'transition-opacity active:opacity-90',
        className,
      )}
    >
      <div className={cn('relative w-full', aspectClass)}>
        {avatar.thumbnailUrl && (
          <img
            src={avatar.thumbnailUrl}
            alt={avatar.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
        {/* Badges row, top-left of the tile — visible against the image. */}
        <AvatarBadges
          avatar={avatar}
          size={isGrid ? 'compact' : 'inline'}
          className={cn('absolute left-2 top-2', isGrid && 'max-w-[calc(100%-1rem)]')}
        />
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent',
            isGrid ? 'p-2' : 'p-4',
          )}
        >
          <h3
            className={cn(
              'truncate font-semibold text-white',
              isGrid ? 'text-xs' : 'text-base',
            )}
          >
            {avatar.title}
          </h3>
        </div>
      </div>
    </Link>
  );
}
